import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, ArrowRight, Trophy } from 'lucide-react'
import { ManagementCompanyNavigation } from '@/components'
import { Card } from '@/components/ui'
import { NetworkFixedCdCard, PeriodSelector } from '@/components/analytics'
import { LoadingScreen } from '@/components/LoadingScreen'
import { RatingsBoard } from '@/components/ratings/RatingsBoard'
import { useAuthStore, useStudioStore } from '@/store'
import { getSupabase } from '@/lib/supabase'
import { withAuthRecovery } from '@/lib/authRecovery'
import { getLocalIsoDate } from '@/lib/analytics/periodCalculations'
import { buildEmptyDataset, loadUnifiedManagementRatings, type UnifiedRatingsData } from '@/lib/ratings/unifiedRatings'
import { getCurrentWeekNetworkFixedCd, type CurrentWeekNetworkFixedCdSnapshot } from '@/services/networkFixedCdService'
import { formatCurrency, type MetricPeriod } from '@/lib/utils'

type PeriodRangeKey = 'period' | 'yesterday' | `week-${number}`

interface MetricWeek {
  week_id: number
  period_id: number
  week_number_in_period: number
  week_start: string
  week_end: string
  week_len_days: number
}

interface ListokPlanSummary {
  plan: number
  factToDate: number
  remaining: number
  remainingPerStudio: number
  remainingPerStudioPerDay: number
  completionPct: number
  elapsedPlanPct: number
  isPastRange: boolean
}

function addDaysToIsoDate(dateString: string, days: number): string {
  const date = new Date(`${dateString}T00:00:00`)
  date.setDate(date.getDate() + days)
  return getLocalIsoDate(date)
}

function getInclusiveDays(startDate: string, endDate: string): number {
  const start = new Date(`${startDate}T00:00:00`)
  const end = new Date(`${endDate}T00:00:00`)
  const diff = end.getTime() - start.getTime()
  return Math.max(0, Math.floor(diff / (1000 * 60 * 60 * 24)) + 1)
}

function getRemainingDaysInRange(startDate: string, endDate: string, today: string): number {
  if (today > endDate) return 0
  const remainingStart = today < startDate ? startDate : today
  return getInclusiveDays(remainingStart, endDate)
}

function toFiniteNumber(value: unknown): number {
  const numberValue = Number(value)
  return Number.isFinite(numberValue) ? numberValue : 0
}

function formatPercent(value: number): string {
  return `${value.toLocaleString('ru-RU', {
    maximumFractionDigits: 1
  })}%`
}

function formatCompactCurrency(value: number): string {
  const abs = Math.abs(value)
  if (abs >= 1_000_000) {
    return `${(value / 1_000_000).toLocaleString('ru-RU', {
      maximumFractionDigits: 1
    })} млн ₽`
  }
  if (abs >= 1_000) {
    return `${Math.round(value / 1_000).toLocaleString('ru-RU')} тыс. ₽`
  }
  return formatCurrency(value)
}


async function fetchListokPaymentsForRange(
  supabase: ReturnType<typeof getSupabase>,
  startDate: string,
  endDate: string
): Promise<Array<{ date: string | null; amount_rub: number | string | null }>> {
  const pageSize = 1000
  const rows: Array<{ date: string | null; amount_rub: number | string | null }> = []
  let pageFrom = 0

  while (true) {
    const { data, error } = await withAuthRecovery(() =>
      supabase
        .from('report_payments_with_rub')
        .select('date, amount_rub')
        .gte('date', startDate)
        .lte('date', endDate)
        .order('date', { ascending: true })
        .order('id', { ascending: true })
        .range(pageFrom, pageFrom + pageSize - 1)
    ) as {
      data: Array<{ date: string | null; amount_rub: number | string | null }> | null
      error: { message?: string } | null
    }

    if (error) {
      throw new Error(error.message || 'Не удалось загрузить факт Listok')
    }

    const batch = data || []
    rows.push(...batch)
    if (batch.length < pageSize) break
    pageFrom += batch.length
  }

  return rows
}

function ListokPlanMiniPanel({ summary }: { summary: ListokPlanSummary }) {
  const completionWidth = Math.min(Math.max(summary.completionPct, 0), 100)
  const paceWidth = Math.min(Math.max(summary.elapsedPlanPct, 0), 100)

  return (
    <div className="rounded-lg border border-border/60 bg-muted/30 px-1.5 py-1">
      <div className="flex min-w-0 items-center gap-2">
        <div className="relative h-1.5 min-w-0 flex-1 overflow-visible rounded-full bg-muted ring-1 ring-border/70">
          <div className="h-full rounded-full bg-amber-400" style={{ width: `${completionWidth}%` }} />
          <span
            className="absolute top-[-3px] h-3 w-[2px] rounded-full bg-slate-700"
            style={{ left: `${paceWidth}%`, transform: 'translateX(-50%)' }}
          />
        </div>
        <span className="w-11 text-right text-[11px] font-bold tabular-nums text-amber-900">
          {formatPercent(summary.completionPct)}
        </span>
      </div>

      <div className="mt-1 flex min-w-0 flex-wrap gap-x-2 gap-y-0.5 text-[10px] leading-tight text-muted-foreground">
        <span>План <b className="font-semibold text-foreground">{formatCompactCurrency(summary.plan)}</b></span>
        <span>Сделано <b className="font-semibold text-foreground">{formatCompactCurrency(summary.factToDate)}</b></span>
        <span>Ост. <b className="font-semibold text-foreground">{formatCompactCurrency(summary.remaining)}</b></span>
        <span>На ст. <b className="font-semibold text-foreground">{formatCompactCurrency(summary.remainingPerStudio)}</b></span>
        <span>День/ст. <b className="font-semibold text-foreground">{summary.isPastRange ? '—' : formatCompactCurrency(summary.remainingPerStudioPerDay)}</b></span>
      </div>
    </div>
  )
}

export function UnifiedRatingsPrototype() {
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const { studios, partners, isManagementCompany } = useStudioStore()

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [availablePeriods, setAvailablePeriods] = useState<MetricPeriod[]>([])
  const [currentPeriod, setCurrentPeriod] = useState<MetricPeriod | null>(null)
  const [currentPeriodIndex, setCurrentPeriodIndex] = useState(0)
  const [metricWeeks, setMetricWeeks] = useState<MetricWeek[]>([])
  const [selectedRangeKey, setSelectedRangeKey] = useState<PeriodRangeKey>('period')
  const [data, setData] = useState<UnifiedRatingsData>(() => buildEmptyDataset())
  const [dataLoading, setDataLoading] = useState(true)
  const [listokPlanSummary, setListokPlanSummary] = useState<ListokPlanSummary | null>(null)
  const [networkFixedCd, setNetworkFixedCd] = useState<CurrentWeekNetworkFixedCdSnapshot | null>(null)

  const partnerLabelMap = useMemo(
    () => new Map(partners.map((partner) => [partner.partner_number, partner.label])),
    [partners]
  )

  useEffect(() => {
    if (!isManagementCompany()) navigate('/')
  }, [isManagementCompany, navigate])

  // Периоды
  useEffect(() => {
    const loadPeriods = async () => {
      if (!user?.id || !isManagementCompany()) return
      const supabase = getSupabase()
      const today = getLocalIsoDate()
      const { data: rows, error: periodsError } = await withAuthRecovery(() =>
        supabase.from('metric_periods').select('*').lte('period_start', today).order('period_start', { ascending: true })
      ) as { data: MetricPeriod[] | null; error: { message?: string } | null }

      if (periodsError || !rows || rows.length === 0) {
        setAvailablePeriods([])
        setCurrentPeriod(null)
        setLoading(false)
        return
      }
      const currentIndex = rows.findIndex((p) => p.period_start <= today && p.period_end >= today)
      const selectedIndex = currentIndex >= 0 ? currentIndex : rows.length - 1
      setAvailablePeriods(rows)
      setCurrentPeriodIndex(selectedIndex)
      setCurrentPeriod(rows[selectedIndex])
      setLoading(false)
    }
    loadPeriods()
  }, [user?.id, isManagementCompany])

  // Недели выбранного периода
  useEffect(() => {
    if (!currentPeriod?.period_id) {
      setMetricWeeks([])
      return
    }
    let cancelled = false
    const supabase = getSupabase()
    withAuthRecovery(() =>
      supabase
        .from('metric_weeks')
        .select('week_id, period_id, week_number_in_period, week_start, week_end, week_len_days')
        .eq('period_id', currentPeriod.period_id)
        .order('week_number_in_period', { ascending: true })
    ).then(({ data: rows }) => {
      if (!cancelled) setMetricWeeks((rows as MetricWeek[]) || [])
    })
    return () => { cancelled = true }
  }, [currentPeriod?.period_id])

  const effectivePeriodEnd = useMemo(() => {
    if (!currentPeriod) return null
    const today = getLocalIsoDate()
    return today < currentPeriod.period_end ? today : currentPeriod.period_end
  }, [currentPeriod])

  const yesterdayInCurrentPeriod = useMemo(() => {
    if (!currentPeriod) return null
    const today = getLocalIsoDate()
    if (!(currentPeriod.period_start <= today && currentPeriod.period_end >= today)) return null
    const rawYesterday = new Date(`${today}T00:00:00`)
    rawYesterday.setDate(rawYesterday.getDate() - 1)
    const yesterday = getLocalIsoDate(rawYesterday)
    if (yesterday < currentPeriod.period_start || yesterday > currentPeriod.period_end) return null
    return yesterday
  }, [currentPeriod])

  const selectedWeekNumber = selectedRangeKey.startsWith('week-') ? Number(selectedRangeKey.replace('week-', '')) : null
  const selectedMetricWeek = useMemo(
    () => (selectedWeekNumber ? metricWeeks.find((w) => w.week_number_in_period === selectedWeekNumber) || null : null),
    [metricWeeks, selectedWeekNumber]
  )

  const rangeOptions = useMemo(() => {
    const visibleWeeks = metricWeeks
      .filter((week) => !effectivePeriodEnd || week.week_start <= effectivePeriodEnd)
      .sort((a, b) => a.week_number_in_period - b.week_number_in_period)
    return [
      { value: 'period' as PeriodRangeKey, label: 'Весь период' },
      ...(yesterdayInCurrentPeriod ? [{ value: 'yesterday' as PeriodRangeKey, label: 'За вчера' }] : []),
      ...visibleWeeks.map((week) => ({ value: `week-${week.week_number_in_period}` as PeriodRangeKey, label: `Неделя ${week.week_number_in_period}` }))
    ]
  }, [effectivePeriodEnd, metricWeeks, yesterdayInCurrentPeriod])

  useEffect(() => {
    if (!rangeOptions.some((option) => option.value === selectedRangeKey)) setSelectedRangeKey('period')
  }, [rangeOptions, selectedRangeKey])

  const selectedRange = useMemo(() => {
    if (!currentPeriod || !effectivePeriodEnd) return null
    if (selectedRangeKey === 'yesterday' && yesterdayInCurrentPeriod) {
      return { mode: 'yesterday' as const, start: yesterdayInCurrentPeriod, end: yesterdayInCurrentPeriod, label: 'За вчера' }
    }
    if (!selectedMetricWeek) {
      return { mode: 'period' as const, start: currentPeriod.period_start, end: effectivePeriodEnd, label: 'Весь период' }
    }
    return {
      mode: 'week' as const,
      start: selectedMetricWeek.week_start,
      end: selectedMetricWeek.week_end < effectivePeriodEnd ? selectedMetricWeek.week_end : effectivePeriodEnd,
      label: `Неделя ${selectedMetricWeek.week_number_in_period}`
    }
  }, [currentPeriod, effectivePeriodEnd, selectedMetricWeek, selectedRangeKey, yesterdayInCurrentPeriod])

  // Загрузка данных рейтинга
  useEffect(() => {
    let isActive = true
    const load = async () => {
      if (!currentPeriod || !selectedRange) return
      try {
        setDataLoading(true)
        setError(null)
        const today = getLocalIsoDate()
        const clientRangeEnd = selectedRange.mode === 'yesterday'
          ? selectedRange.end
          : selectedMetricWeek
            ? (selectedMetricWeek.week_end < today ? selectedMetricWeek.week_end : today)
            : (currentPeriod.period_end < today ? currentPeriod.period_end : today)
        const clientRangeStart = selectedRange.mode === 'yesterday'
          ? selectedRange.start
          : selectedMetricWeek
            ? selectedMetricWeek.week_start
            : currentPeriod.period_start

        const result = await loadUnifiedManagementRatings({
          studios,
          partnerLabelMap,
          period: currentPeriod,
          rangeStart: selectedRange.start,
          rangeEnd: selectedRange.end,
          clientRangeStart,
          clientRangeEnd,
          rangeMode: selectedRange.mode,
          selectedWeekId: selectedMetricWeek?.week_id ?? null,
        })

        if (!isActive) return
        setData(result)
      } catch (err) {
        if (!isActive) return
        console.error('UnifiedRatingsPrototype:', err)
        setError(err instanceof Error ? err.message : 'Не удалось загрузить рейтинги')
      } finally {
        if (isActive) setDataLoading(false)
      }
    }
    load()
    return () => { isActive = false }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPeriod, selectedRangeKey, studios, partnerLabelMap])

  useEffect(() => {
    let isActive = true

    const loadListokSummary = async () => {
      if (!currentPeriod || metricWeeks.length === 0) {
        setListokPlanSummary(null)
        return
      }

      const managementStudios = studios.filter(
        (studio) =>
          (studio.access_kind === 'management_company' || studio.role === 'management_company')
          && studio.status_code === 'OPEN'
      )

      if (managementStudios.length === 0) {
        setListokPlanSummary(null)
        return
      }

      try {
        const today = getLocalIsoDate()
        const yesterday = addDaysToIsoDate(today, -1)
        const yesterdayWeek = yesterdayInCurrentPeriod
          ? metricWeeks.find((week) => yesterdayInCurrentPeriod >= week.week_start && yesterdayInCurrentPeriod <= week.week_end) || null
          : null
        const summaryWeek = selectedRangeKey === 'yesterday'
          ? yesterdayWeek
          : selectedMetricWeek
        const summaryRangeStart = summaryWeek?.week_start || currentPeriod.period_start
        const summaryRangeEnd = summaryWeek?.week_end || currentPeriod.period_end
        const selectedWeekIds = new Set((summaryWeek ? [summaryWeek] : metricWeeks).map((week) => week.week_id))
        const rangeState =
          summaryRangeEnd < today
            ? 'past'
            : summaryRangeStart > today
              ? 'future'
              : 'current'
        const rawFactEndDate = rangeState === 'current'
          ? today
          : rangeState === 'past'
            ? summaryRangeEnd
            : null
        const factEndDate = rawFactEndDate && rawFactEndDate >= summaryRangeStart
          ? (rawFactEndDate > summaryRangeEnd ? summaryRangeEnd : rawFactEndDate)
          : null

        const supabase = getSupabase()
        const [ukPlanRowsData, listokPaymentRows] = await Promise.all([
          withAuthRecovery(() =>
            supabase
              .from('uk_weekly_plan')
              .select('week_id, plan_revenue_rub')
          ) as Promise<{
            data: Array<{ week_id: number; plan_revenue_rub: number | null }> | null
            error: { message?: string } | null
          }>,
          fetchListokPaymentsForRange(supabase, summaryRangeStart, summaryRangeEnd)
        ])

        if (ukPlanRowsData.error) {
          throw new Error(ukPlanRowsData.error.message || 'Не удалось загрузить план УК')
        }

        const plan = (ukPlanRowsData.data || [])
          .filter((row) => selectedWeekIds.has(row.week_id))
          .reduce((sum, row) => sum + toFiniteNumber(row.plan_revenue_rub), 0)
        const factToDate = factEndDate
          ? listokPaymentRows
              .filter((row) => row.date && row.date >= summaryRangeStart && row.date <= factEndDate)
              .reduce((sum, row) => sum + toFiniteNumber(row.amount_rub), 0)
          : 0
        const rangeDays = Math.max(1, getInclusiveDays(summaryRangeStart, summaryRangeEnd))
        const rawPaceDate = rangeState === 'current'
          ? yesterday
          : rangeState === 'past'
            ? summaryRangeEnd
            : null
        const paceDate = rawPaceDate && rawPaceDate >= summaryRangeStart
          ? (rawPaceDate > summaryRangeEnd ? summaryRangeEnd : rawPaceDate)
          : null
        const elapsedDaysToPaceDate = paceDate ? getInclusiveDays(summaryRangeStart, paceDate) : 0
        const elapsedPlanRatio = Math.min(1, Math.max(0, elapsedDaysToPaceDate / rangeDays))
        const remaining = Math.max(plan - factToDate, 0)
        const daysRemaining = getRemainingDaysInRange(summaryRangeStart, summaryRangeEnd, today)
        const activeStudiosCount = managementStudios.length
        const remainingPerStudio = activeStudiosCount > 0 ? Math.round(remaining / activeStudiosCount) : 0
        const remainingPerStudioPerDay = activeStudiosCount > 0 && daysRemaining > 0
          ? Math.round(remaining / activeStudiosCount / daysRemaining)
          : 0

        if (!isActive) return
        setListokPlanSummary({
          plan,
          factToDate,
          remaining,
          remainingPerStudio,
          remainingPerStudioPerDay,
          completionPct: plan > 0 ? (factToDate / plan) * 100 : 0,
          elapsedPlanPct: elapsedPlanRatio * 100,
          isPastRange: rangeState === 'past'
        })
      } catch (err) {
        if (!isActive) return
        console.error('UnifiedRatingsPrototype listok summary:', err)
        setListokPlanSummary(null)
      }
    }

    loadListokSummary()

    return () => {
      isActive = false
    }
  }, [currentPeriod, metricWeeks, selectedMetricWeek, selectedRangeKey, studios, yesterdayInCurrentPeriod])

  useEffect(() => {
    let isActive = true

    const loadNetworkFixedCd = async () => {
      if (!currentPeriod || !selectedRange || selectedRange.mode !== 'week' || !selectedWeekNumber) {
        setNetworkFixedCd(null)
        return
      }

      const today = getLocalIsoDate()
      const isCurrentWeek = selectedRange.start <= today && selectedRange.end >= today
      if (!isCurrentWeek) {
        setNetworkFixedCd(null)
        return
      }

      try {
        const snapshot = await getCurrentWeekNetworkFixedCd(currentPeriod.period_id, selectedWeekNumber)
        if (isActive) {
          setNetworkFixedCd(snapshot)
        }
      } catch (err) {
        if (isActive) {
          console.error('UnifiedRatingsPrototype network fixed cd:', err)
          setNetworkFixedCd(null)
        }
      }
    }

    loadNetworkFixedCd()

    return () => {
      isActive = false
    }
  }, [currentPeriod, selectedRange, selectedWeekNumber])

  const handlePeriodChange = (direction: 'prev' | 'next') => {
    const newIndex = direction === 'prev' ? currentPeriodIndex - 1 : currentPeriodIndex + 1
    if (newIndex >= 0 && newIndex < availablePeriods.length) {
      setCurrentPeriodIndex(newIndex)
      setCurrentPeriod(availablePeriods[newIndex])
      setSelectedRangeKey('period')
    }
  }

  const canGoNext = () => {
    if (currentPeriodIndex >= availablePeriods.length - 1) return false
    const nextPeriod = availablePeriods[currentPeriodIndex + 1]
    return Boolean(nextPeriod && nextPeriod.period_start <= getLocalIsoDate())
  }

  if (loading) return <LoadingScreen />

  if (!currentPeriod) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center p-4 text-sm text-muted-foreground">
        Не удалось определить период для рейтинга.
      </div>
    )
  }

  const sideHeader = (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => navigate('/management-dashboard')}
          className="inline-flex h-7 w-7 flex-none items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          aria-label="Назад в дашборд"
          title="Назад в дашборд"
        >
          <ArrowLeft className="h-4 w-4" />
        </button>
        {/* Иконка «кубок» — сплошная акцентная заливка на ролях: пара
            `text-amber-950` + `to-amber-500` в тёмной теме давала 2,07:1
            (оттенок 500 самозеркальный, текст уезжает в почти-белый). */}
        <span className="inline-flex h-7 w-7 flex-none items-center justify-center rounded-lg bg-primary text-primary-foreground shadow-sm">
          <Trophy className="h-3.5 w-3.5" />
        </span>
        <div className="min-w-0">
          <h1 className="truncate text-sm font-semibold tracking-tight">Единый рейтинг сети</h1>
        </div>
      </div>
      <PeriodSelector
        compact
        stacked
        currentPeriod={currentPeriod}
        onPrevPeriod={() => handlePeriodChange('prev')}
        onNextPeriod={() => handlePeriodChange('next')}
        canGoPrev={currentPeriodIndex > 0}
        canGoNext={canGoNext()}
        showViewModeSelector
        isCurrentPeriod={currentPeriod.period_start <= getLocalIsoDate() && currentPeriod.period_end >= getLocalIsoDate()}
        periodFilterValue={selectedRangeKey}
        periodFilterOptions={rangeOptions}
        onPeriodFilterChange={(value) => setSelectedRangeKey(value as PeriodRangeKey)}
        selectedRange={selectedRange as never}
      />
      {listokPlanSummary ? (
        <ListokPlanMiniPanel summary={listokPlanSummary} />
      ) : null}
      {networkFixedCd ? (
        <NetworkFixedCdCard snapshot={networkFixedCd} compact />
      ) : null}
    </div>
  )

  return (
    <div className="flex h-full min-h-0 flex-col pb-20">
      {error ? (
        <Card className="mb-2 border border-destructive/20 bg-destructive/5">
          <p className="text-sm text-destructive">{error}</p>
        </Card>
      ) : null}

      {dataLoading ? (
        <Card className="flex min-h-[30vh] items-center justify-center text-sm text-muted-foreground">Загрузка рейтингов…</Card>
      ) : (
        <RatingsBoard
          data={data}
          mode="explorer"
          compact
          sideHeader={sideHeader}
          rightHeaderAction={
            <button
              type="button"
              onClick={() => navigate('/management-ratings/subscriptions')}
              className="group inline-flex items-center gap-1.5 rounded-md border border-border/70 bg-background/70 px-2.5 py-1 text-[11px] font-medium text-muted-foreground shadow-sm transition-colors hover:border-primary/35 hover:bg-muted hover:text-foreground"
            >
              Рейтинги по подпискам
              <ArrowRight className="h-3.5 w-3.5 flex-none transition-transform group-hover:translate-x-0.5" />
            </button>
          }
          hiddenMetricKeys={selectedRange?.mode === 'yesterday' ? ['classic_group_index'] : []}
        />
      )}
      <ManagementCompanyNavigation />
    </div>
  )
}
